import React, { memo, useCallback, useMemo, useRef, useState, useEffect } from "react";
import { ReportData } from "../../types/global.d";
import { ReportsListItem } from "../ReportsListItem";
import FilterSortHeader from "./FilterSortHeader";
import ReportsEmptyState from "./ReportsEmptyState";
import { useUserContext } from "../../contexts/UserContext";
import { UserStatus } from "../../constants";
import {
  filterReportsBySearch,
  filterReportsByStatus,
  sortReportsByPriority,
  FilterType,
} from "./reportsUtils";

interface ReportsListContentProps {
  reports: ReportData[];
  onReportClick: (report: ReportData) => void;
  selectedReportId?: string | null;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  selectedFilter: FilterType;
  onFilterChange: (filter: FilterType) => void;
  selectedItemRef: React.RefObject<HTMLDivElement | null>;
  onClose?: () => void;
  userFilter?: string | null;
  onUserFilterChange?: (nickname: string | null) => void;
}

const ReportsListContent: React.FC<ReportsListContentProps> = memo(
  ({
    reports,
    onReportClick,
    selectedReportId,
    searchTerm,
    onSearchChange,
    selectedFilter,
    onFilterChange,
    selectedItemRef,
    onClose,
    userFilter,
    onUserFilterChange,
  }) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [showScrollToTop, setShowScrollToTop] = useState(false);
    const { user } = useUserContext();
    const isAdmin = user?.status === UserStatus.admin;

    // Memoize the processed and sorted reports
    const processedReports = useMemo(() => {
      // Filter by search term first
      let filteredReports = filterReportsBySearch(reports, searchTerm);

      // Filter by user if userFilter is set
      if (userFilter) {
        filteredReports = filteredReports.filter(
          (report) =>
            report.user?.nickname === userFilter ||
            report.cleaned?.user?.nickname === userFilter
        );
      }

      // Then filter by cleaning status
      const statusFilteredReports = filterReportsByStatus(
        filteredReports,
        selectedFilter
      );

      // Sort by moderation priority for admins, then freshness.
      return sortReportsByPriority(statusFilteredReports, isAdmin);
    }, [reports, searchTerm, selectedFilter, userFilter, isAdmin]);

    const handleReportClick = useCallback(
      (report: ReportData) => {
        onReportClick(report);
      },
      [onReportClick]
    );

    // Track scroll position to show/hide scroll-to-top button
    useEffect(() => {
      const scrollContainer = scrollContainerRef.current;
      if (!scrollContainer) return;

      const handleScroll = () => {
        const scrollThreshold = 300; // Show button after scrolling 300px
        setShowScrollToTop(scrollContainer.scrollTop > scrollThreshold);
      };

      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }, []);

    // Scroll to top handler
    const handleScrollToTop = useCallback(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }, []);

    return (
      <div className="reports-list">
        <FilterSortHeader
          selectedFilter={selectedFilter}
          onFilterChange={onFilterChange}
          searchTerm={searchTerm}
          onSearchChange={onSearchChange}
          reportsCount={processedReports.length}
          onClose={onClose}
          userFilter={userFilter}
          onUserFilterChange={onUserFilterChange}
          showScrollToTop={showScrollToTop}
          onScrollToTop={handleScrollToTop}
        />

        <div className="reports-list-content" ref={scrollContainerRef}>
          {processedReports.length === 0 ? (
            <ReportsEmptyState hasSearchTerm={!!searchTerm} />
          ) : (
            processedReports.map((report) => (
              <ReportsListItem
                key={report._id}
                report={report}
                onClick={() => handleReportClick(report)}
                isSelected={selectedReportId === report._id}
                ref={selectedReportId === report._id ? selectedItemRef : null}
              />
            ))
          )}
        </div>
      </div>
    );
  }
);

ReportsListContent.displayName = "ReportsListContent";

export default ReportsListContent;
